'use client';

import React, { useMemo } from 'react';
import { Button } from 'src/components';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useGetProviderCohortByIdQuery } from 'src/api/redux/providerCohorts';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSliders, faFileInvoice } from '@fortawesome/pro-solid-svg-icons';
import { useAppSelector } from 'src/redux';
import { EnrichingCodesInfo } from 'src/components/PulseUI';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { exists } from 'src/utils/exists/exists';

const NewGroupHeader = ({ field, tableType, ...props }) => {
  const { dataCoverage: dataCoverageFlag } = useFlags();
  const { closeAllButCurrent } = useDrawer();
  const { setColumnId } = useTableFilterSelections(tableType);

  // Use RTK Query directly for code sets data
  const { data: filterSetsData } = useGetFilterSetsQuery({ q: '', serializer: 'detailed' });
  const allCodeSets = useMemo(() => {
    return filterSetsData?.results?.filter((set) => set.type === 'CODE') || [];
  }, [filterSetsData]);

  const id = field?.split('_')[0] || props?.groupId?.split('_')[0];
  const { data: selectedCohort } = useGetProviderCohortByIdQuery(id, {
    skip: !id,
  });

  const currentCodeSet = useMemo(() => {
    // Extract base ID from composite key (for NewTreatingTable compatibility)
    const extractBaseId = (key) => {
      if (!key) return null;
      return key.split('_')[0];
    };

    const baseField = extractBaseId(field);
    const baseGroupId = extractBaseId(props?.groupId);

    return allCodeSets?.find(
      (codeSet) =>
        codeSet.id.toString() === baseField ||
        codeSet.id.toString() === baseGroupId ||
        codeSet.id.toString() === field ||
        codeSet.id.toString() === props?.groupId
    );
  }, [field, props?.groupId, allCodeSets]);

  const dataCoverage = currentCodeSet?.detailed_filters?.flatMap(
    (filter) =>
      filter.detailed_values?.map((value: any) => value.vrdc_enriched) ?? []
  );

  // Get the column settings from Redux store
  const columnSettings = useAppSelector((state) => {
    // Get the specific column settings for this field
    const tableState = state.filterSelections.tables[tableType];
    if (!tableState) return { filter_sets: [] };

    return field && tableState.appliedColumnSettings[field]
      ? tableState.appliedColumnSettings[field]
      : { filter_sets: [] };
  });

  // Get the billing filter sets for this specific column
  const columnBillingFilters = columnSettings.filter_sets || [];

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Get the column ID from the field prop or groupId
    const columnId = field || props?.groupId;

    // Set the column ID with isEditing=true to enable editing mode (like EncountersHeaderVolume)
    if (columnId) {
      setColumnId(columnId, undefined, true);
      setTimeout(() => {
        closeAllButCurrent(`table-volume-${tableType}`);
      }, 100);
    } else {
      closeAllButCurrent(`table-volume-${tableType}`);
    }
  };

  return (
    <Button
      size="sm"
      variant="outlined"
      className="w-full rounded-full bg-white border-teal-400 text-primary px-3 "
      onClick={handleClick}
      startDecorator={
        <Icon icon={faFileInvoice} className="text-neutral-600" />
      }
      endDecorator={
        <div
          className={`flex items-center justify-center gap-1 rounded-full px-2 py-0.5 text-xs ${
            columnBillingFilters.length === 0
              ? 'bg-teal-050 text-primary'
              : 'bg-teal-600 text-white'
          }`}
        >
          <Icon icon={faSliders} /> {columnBillingFilters.length}
        </div>
      }
    >
      <div className="flex items-center gap-1 min-w-0 max-w-full">
        <div className="truncate">{props?.headerName}</div>
        {dataCoverageFlag && (
          <EnrichingCodesInfo
            dataCoverage={dataCoverage}
            iconOnly={true}
            isEncounter={exists(selectedCohort)}
          />
        )}
      </div>
    </Button>
  );
};

export default NewGroupHeader;
